<template>
  <div class="tt-page">
    <div class="tt-header">
      <div class="tt-header-left">
        <h1 class="tt-title">Text templates</h1>
        <p class="tt-subtitle">Manage all text templates in one place</p>
      </div>
      <button class="tt-btn-primary" @click="openCreateDialog">
        <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"/>
        </svg>
        New template
      </button>
    </div>

    <TemplateList
      :templates="templates"
      :loading="loading"
      @edit="openEditDialog"
      @delete="openDeleteDialog"
      @toggle-active="toggleActive"
    />

    <TemplateFormDialog
      v-if="formDialog"
      :template="selectedTemplate"
      :loading="formLoading"
      @save="saveTemplate"
      @close="formDialog = false"
    />

    <TemplateDeleteDialog
      v-if="deleteDialog"
      :template="selectedTemplate"
      :loading="deleteLoading"
      @confirm="deleteTemplate"
      @close="deleteDialog = false"
    />

    <transition name="tt-toast">
      <div v-if="snackbar.show" class="tt-toast" :class="'tt-toast--' + snackbar.color">
        <svg v-if="snackbar.color === 'success'" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
        </svg>
        <svg v-else width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
        {{ snackbar.message }}
      </div>
    </transition>
  </div>
</template>

<script>
import TemplateList from './components/TemplateList.vue'
import TemplateFormDialog from './components/TemplateFormDialog.vue'
import TemplateDeleteDialog from './components/TemplateDeleteDialog.vue'
import ApiService from "@/core/services/api.service"

export default {
  name: 'TextTemplates',

  components: { TemplateList, TemplateFormDialog, TemplateDeleteDialog },

  data() {
    return {
      templates: [],
      loading: false,
      formDialog: false,
      deleteDialog: false,
      formLoading: false,
      deleteLoading: false,
      selectedTemplate: null,
      snackbar: { show: false, message: '', color: 'success' },
    }
  },

  mounted() {
    this.fetchTemplates()
  },

  methods: {
    async fetchTemplates() {
      this.loading = true
      try {
        const { data } = await ApiService.get(
          "inventory/text-templates",
          "?context=sales_order.customer_comment"
        )
        this.templates = Array.isArray(data) ? data : (data.data || [])
      } catch (e) {
        this.showSnackbar('Error loading templates', 'error')
      } finally {
        this.loading = false
      }
    },

    openCreateDialog() {
      this.selectedTemplate = null
      this.formDialog = true
    },

    openEditDialog(template) {
      this.selectedTemplate = { ...template }
      this.formDialog = true
    },

    openDeleteDialog(template) {
      this.selectedTemplate = template
      this.deleteDialog = true
    },

    async saveTemplate(form) {
      this.formLoading = true
      try {
        if (form.id) {
          const { data } = await ApiService.put(`inventory/text-templates/${form.id}`, form)
          const idx = this.templates.findIndex(t => t.id === form.id)
          if (idx !== -1) this.$set(this.templates, idx, data)
          this.showSnackbar('Template successfully updated')
        } else {
          const { data } = await ApiService.post('inventory/text-templates', form)
          this.templates.push(data)
          this.showSnackbar('Template successfully created')
        }
        this.formDialog = false
      } catch (e) {
        this.showSnackbar('Error during saving', 'error')
      } finally {
        this.formLoading = false
      }
    },

    async deleteTemplate() {
      this.deleteLoading = true
      try {
        await ApiService.delete(`inventory/text-templates/${this.selectedTemplate.id}`)
        this.templates = this.templates.filter(t => t.id !== this.selectedTemplate.id)
        this.showSnackbar('Sablon törölve')
        this.deleteDialog = false
      } catch (e) {
        this.showSnackbar('Error during deletion', 'error')
      } finally {
        this.deleteLoading = false
      }
    },

    async toggleActive(template) {
      try {
        const { data } = await ApiService.patch(`inventory/text-templates/${template.id}`, {
          is_active: !template.is_active,
        })
        const idx = this.templates.findIndex(t => t.id === template.id)
        if (idx !== -1) this.$set(this.templates, idx, data)
        this.showSnackbar(data.is_active ? 'Template enabled' : 'Template deactivated')
      } catch (e) {
        this.showSnackbar('Error when changing status', 'error')
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbar = { show: true, message, color }
      setTimeout(() => { this.snackbar.show = false }, 3000)
    },
  },
}
</script>

<style scoped>
.tt-page {
  padding: 2rem 2.5rem;
  background: #f5f7fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  position: relative;
}

.tt-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.75rem;
}

.tt-title {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 0 0 4px;
}

.tt-subtitle {
  font-size: 13.5px;
  color: #64748b;
  margin: 0;
}

.tt-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 18px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}
.tt-btn-primary:hover { background: #1d4ed8; }

.tt-toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 500;
  z-index: 9999;
  box-shadow: 0 4px 20px rgba(0,0,0,0.12);
}
.tt-toast--success { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
.tt-toast--error { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }

.tt-toast-enter-active, .tt-toast-leave-active { transition: all 0.25s ease; }
.tt-toast-enter, .tt-toast-leave-to { opacity: 0; transform: translateY(10px); }
</style>